import {LiveKitRoom} from '@livekit/components-react'
import type {RoomOptions} from 'livekit-client'
import {LIVEKIT_URL} from '@/shared/config'
import type {RoomPageState} from '../model/useRoomPage'
import {PreJoinLobby} from '@/pages/room/pre-join-lobby'
import {ConnectedRoomGuard} from './ConnectedRoomGuard'
import {RoomLoadingState} from './RoomLoadingState'
import {RoomErrorState} from './RoomErrorState'

const ROOM_OPTIONS: RoomOptions = {
  audioCaptureDefaults: {
    autoGainControl: true,
    noiseSuppression: true,
    echoCancellation: true,
  },
  publishDefaults: {
    dtx: true,
  },
}

interface RoomPhaseContentProps {
  state: RoomPageState
  onJoin: (username: string, audioEnabled: boolean, videoEnabled: boolean) => void
  onLeave: () => void
  onRequestFocusHint?: () => void
  roomId: string
  focusHintOpen?: boolean
  onFocusHintOpenChange?: (open: boolean) => void
}

/**
 * Renders room UI by phase: lobby, loading, error, or connected room.
 */
export function RoomPhaseContent({
  state,
  onJoin,
  onLeave,
  onRequestFocusHint,
  roomId,
  focusHintOpen,
  onFocusHintOpenChange,
}: RoomPhaseContentProps) {
  if (state.phase === 'ready') {
    return (
      <LiveKitRoom
        token={state.token}
        serverUrl={LIVEKIT_URL}
        connect={true}
        audio={state.joinData.audioEnabled}
        video={state.joinData.videoEnabled}
        options={ROOM_OPTIONS}
        className="flex min-h-0 flex-1 flex-col"
      >
        <ConnectedRoomGuard
          roomId={roomId}
          onLeave={onLeave}
          focusHintOpen={focusHintOpen}
          onFocusHintOpenChange={onFocusHintOpenChange}
          onRequestFocusHint={onRequestFocusHint}
        />
      </LiveKitRoom>
    )
  }
  if (state.phase === 'lobby') {
    return <PreJoinLobby onJoin={onJoin} />
  }
  if (state.phase === 'error') {
    return <RoomErrorState message={state.errorMessage} />
  }
  return <RoomLoadingState />
}
